import React, { useState, useRef, useEffect } from 'react';
import { ArrowUp, Plus, Image, Video, Sparkles, Wand2, Palette, Zap, Trash2 } from 'lucide-react';
import { spacingMobile, borderRadius, shadows } from '../../constants/designTokens';
import { useTheme } from '../../contexts/ThemeContext';
import { RENDER_CONFIG } from '../../constants/renderConfig';
import brandIcon from '../../assets/ai-image-ascii.png';
import ChatMessageList from './ChatMessageList';
import ASCIIPreview from './ASCIIPreview';
import SimpleTooltip from '../ui/SimpleTooltip';
import ConfirmModal from '../modals/ConfirmModal';

/**
 * Custom brand icon
 */
const BrandIcon = ({ size = 28 }) => (
  <img
    src={brandIcon}
    alt=""
    width={size}
    height={size}
    style={{ objectFit: 'contain' }}
  />
);

/**
 * Get time-based greeting
 */
const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour >= 5 && hour < 12) return 'Good to see you';
  if (hour >= 12 && hour < 17) return 'Good afternoon';
  if (hour >= 17 && hour < 21) return 'Good evening';
  return 'Hello, night owl';
};

/**
 * Quick action suggestions for mobile
 */
const quickActions = [
  { icon: Sparkles, label: 'Innovation', prompt: 'Innovation emerging from collaboration' },
  { icon: Wand2, label: 'Abstract', prompt: 'Abstract geometric patterns flowing like water' },
  { icon: Palette, label: 'Nature', prompt: 'Organic growth patterns inspired by nature' },
  { icon: Zap, label: 'Energy', prompt: 'Dynamic energy bursting with momentum' },
];

/**
 * MobileCreativePromptView Component
 *
 * Mobile-optimized Claude-like chat interface for AI pattern generation.
 * Features larger touch targets, vertical layout, and better spacing.
 */
const MobileCreativePromptView = ({
  prompt,
  onPromptChange,
  isGenerating,
  isDisabled = false,
  disabledUntil = null,
  onGenerate,
  onImageUpload,
  onVideoUpload,
  messages = [],
  selectedMessageId = null,
  onSelectMessage = () => {},
  // ASCII Preview props for selected message
  asciiText = null,
  columns,
  isFullscreen,
  onFullscreenClose,
  onFreeze,
  onUnfreeze,
  isFrozen,
  backgroundColor,
  foregroundColor,
  // When true, text generation is locked because a custom media-based pattern exists
  hasUploadedMedia = false,
  uploadedImageUrl = null,
  uploadedVideoUrl = null,
  isMediaReady = false,
  // Clear history handler
  onClearHistory = () => {},
}) => {
  const { colors } = useTheme();
  const [showAttachMenu, setShowAttachMenu] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const textareaRef = useRef(null);
  const scrollRef = useRef(null);
  const hasHistory = messages.length > 0;

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (scrollRef.current && hasHistory) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length, hasHistory]);

  // Calculate remaining time if disabled
  const [remainingTime, setRemainingTime] = useState(null);

  useEffect(() => {
    if (isDisabled && disabledUntil) {
      const updateTimer = () => {
        const now = Date.now();
        const remaining = Math.max(0, disabledUntil - now);
        if (remaining > 0) {
          const minutes = Math.floor(remaining / 60000);
          const seconds = Math.floor((remaining % 60000) / 1000);
          setRemainingTime(`${minutes}:${seconds.toString().padStart(2, '0')}`);
        } else {
          setRemainingTime(null);
        }
      };

      updateTimer();
      const interval = setInterval(updateTimer, 1000);
      return () => clearInterval(interval);
    } else {
      setRemainingTime(null);
    }
  }, [isDisabled, disabledUntil]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 150) + 'px';
    }
  }, [prompt]);

  const handleQuickAction = (actionPrompt) => {
    onPromptChange(actionPrompt);
    textareaRef.current?.focus();
  };

  const canSubmit = prompt.trim() && !isGenerating && !isDisabled && !hasUploadedMedia;

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      height: hasHistory ? '100%' : 'auto',
      boxSizing: 'border-box',
    }}>
      {/* Greeting - only show if no history */}
      {!hasHistory && (
        <>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: spacingMobile.sm,
            marginBottom: spacingMobile.md,
            padding: `0 ${spacingMobile.md}`,
          }}>
            <BrandIcon size={28} />
            <h1 style={{
              fontSize: '1.5rem',
              fontWeight: 400,
              color: colors.foreground,
              letterSpacing: '-0.02em',
              margin: 0,
              fontFamily: 'Georgia, "Times New Roman", serif',
            }}>
              {getGreeting()}
            </h1>
          </div>

          {/* Info section */}
          <p style={{
            fontSize: '0.875rem',
            color: colors.foregroundSecondary,
            textAlign: 'center',
            lineHeight: 1.5,
            marginBottom: spacingMobile.lg,
            padding: `0 ${spacingMobile.md}`,
          }}>
            Type a vibe, upload media and I'll generate an ASCII pattern.
          </p>
        </>
      )}

      {/* Show ASCII preview for uploaded media when no chat history */}
      {asciiText && !hasHistory && (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: spacingMobile.lg, marginBottom: spacingMobile.lg }}>
          <ASCIIPreview
            asciiText={asciiText}
            columns={columns}
            isFullscreen={isFullscreen}
            onFullscreenClose={onFullscreenClose}
            onFreeze={onFreeze}
            onUnfreeze={onUnfreeze}
            isFrozen={isFrozen}
            backgroundColor={backgroundColor}
            foregroundColor={foregroundColor}
          />
        </div>
      )}

      {/* Chat History - only show if there are messages */}
      {hasHistory && (
        <div
          ref={scrollRef}
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: spacingMobile.md,
          }}
        >
          <ChatMessageList
            messages={messages}
            selectedMessageId={selectedMessageId}
            onSelectMessage={onSelectMessage}
            asciiText={asciiText}
            columns={columns}
            isFullscreen={isFullscreen}
            onFullscreenClose={onFullscreenClose}
            onFreeze={onFreeze}
            onUnfreeze={onUnfreeze}
            isFrozen={isFrozen}
            backgroundColor={backgroundColor}
            foregroundColor={foregroundColor}
          />
        </div>
      )}

      {/* Input Section */}
      <div style={{
        padding: hasHistory ? `${spacingMobile.sm} ${spacingMobile.md} ${spacingMobile.md}` : `0 ${spacingMobile.md}`,
        borderTop: hasHistory ? `1px solid ${colors.border}` : 'none',
        background: hasHistory ? colors.background : 'transparent',
      }}>
        {/* When media is ready, hide input and show minimal reset bar */}
        {isMediaReady ? (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            padding: spacingMobile.md,
          }}>
            <button
              onClick={() => onClearHistory()}
              aria-label="Clear media and start over"
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#FDE8E8';
                e.currentTarget.style.color = '#C0392B';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = colors.backgroundPanel;
                e.currentTarget.style.color = colors.foregroundSecondary;
              }}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: spacingMobile.xs,
                padding: `${spacingMobile.sm} ${spacingMobile.md}`,
                background: colors.backgroundPanel,
                border: `1px solid ${colors.border}`,
                color: colors.foregroundSecondary,
                cursor: 'pointer',
                borderRadius: borderRadius.sm,
                transition: 'all 0.12s ease',
                fontSize: '0.8125rem',
                fontWeight: 500,
              }}
            >
              <Trash2 size={14} />
              Clear media
            </button>
          </div>
        ) : (
          <>
            {/* Chat Input Container */}
            <div style={{
              width: '100%',
              background: colors.backgroundPanel,
              borderRadius: borderRadius.lg,
              border: `1px solid ${isFocused ? colors.foregroundTertiary : colors.border}`,
              boxShadow: isFocused ? shadows.md : shadows.sm,
              transition: 'all 0.2s ease',
            }}>
            {/* Text Input Area */}
            <div style={{
              padding: `${spacingMobile.md} ${spacingMobile.md} ${spacingMobile.sm}`,
            }}>
              {/* Show thumbnail if media is uploading */}
              {hasUploadedMedia && !isMediaReady && (uploadedImageUrl || uploadedVideoUrl) ? (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: spacingMobile.sm,
                  padding: spacingMobile.sm,
                  background: colors.backgroundOffset,
                  borderRadius: borderRadius.sm,
                  marginBottom: spacingMobile.sm,
                }}>
                  <img
                    src={uploadedImageUrl || uploadedVideoUrl}
                    alt="Uploaded media"
                    style={{
                      width: '60px',
                      height: '60px',
                      objectFit: 'cover',
                      borderRadius: borderRadius.sm,
                      border: `1px solid ${colors.border}`,
                    }}
                  />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '0.8125rem', fontWeight: 500, color: colors.foreground, marginBottom: spacingMobile.xs }}>
                      {uploadedImageUrl ? 'Image' : 'Video'} uploaded
                    </div>
                    <div style={{ fontSize: '0.75rem', color: colors.foregroundSecondary }}>
                      Processing...
                    </div>
                  </div>
                </div>
              ) : (
                <textarea
                  ref={textareaRef}
                  value={prompt}
                  onChange={(e) => onPromptChange(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey && canSubmit) {
                      e.preventDefault();
                      onGenerate();
                    }
                  }}
                  onFocus={() => setIsFocused(true)}
                  onBlur={() => setIsFocused(false)}
                  placeholder={hasUploadedMedia ? "Editing uploaded media — text generation disabled" : "How can I help you today?"}
                  disabled={isDisabled || hasUploadedMedia}
                  maxLength={RENDER_CONFIG.maxPromptLength}
                  rows={1}
                  style={{
                    width: '100%',
                    minHeight: '24px',
                    maxHeight: '150px',
                    padding: 0,
                    fontSize: '1rem',
                    fontFamily: 'inherit',
                    lineHeight: '1.5',
                    border: 'none',
                    background: 'transparent',
                    resize: 'none',
                    color: colors.foreground,
                    outline: 'none',
                    overflow: 'auto',
                  }}
                />
              )}

              {hasUploadedMedia && !isMediaReady && !(uploadedImageUrl || uploadedVideoUrl) && (
                <div style={{ fontSize: '0.875rem', color: colors.foregroundSecondary, marginTop: spacingMobile.sm }}>
                  Media uploaded — text generation is disabled. Use the controls to edit the media-based pattern.
                </div>
              )}
            </div>

        {/* Bottom Bar with Controls */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: `${spacingMobile.sm} ${spacingMobile.sm}`,
        }}>
          {/* Left Controls */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: spacingMobile.xs,
            position: 'relative',
          }}>
            {/* Attach Button */}
            <button
              onClick={() => setShowAttachMenu(!showAttachMenu)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '40px',
                height: '40px',
                padding: 0,
                background: showAttachMenu ? colors.backgroundOffset : 'transparent',
                border: `1px solid ${colors.border}`,
                borderRadius: borderRadius.sm,
                cursor: 'pointer',
                color: colors.foregroundSecondary,
                transition: 'all 0.15s ease',
              }}
            >
              <Plus size={22} />
            </button>

            {/* Attach Menu Dropdown */}
            {showAttachMenu && (
              <>
                {/* Backdrop */}
                <div
                  onClick={() => setShowAttachMenu(false)}
                  style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 99,
                  }}
                />
                <div style={{
                  position: 'absolute',
                  bottom: '100%',
                  left: 0,
                  marginBottom: spacingMobile.sm,
                  background: colors.backgroundPanel,
                  borderRadius: borderRadius.md,
                  border: `1px solid ${colors.border}`,
                  boxShadow: shadows.md,
                  overflow: 'hidden',
                  zIndex: 100,
                  minWidth: '180px',
                }}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: spacingMobile.sm,
                    padding: spacingMobile.md,
                    cursor: 'pointer',
                    color: colors.foreground,
                    fontSize: '0.9375rem',
                  }}>
                    <Image size={20} />
                    <span>Upload Image</span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        onImageUpload(e);
                        setShowAttachMenu(false);
                      }}
                      style={{ display: 'none' }}
                    />
                  </label>
                  <div style={{ height: '1px', background: colors.border }} />
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: spacingMobile.sm,
                    padding: spacingMobile.md,
                    cursor: 'pointer',
                    color: colors.foreground,
                    fontSize: '0.9375rem',
                  }}>
                    <Video size={20} />
                    <span>Upload Video</span>
                    <input
                      type="file"
                      accept="video/mp4,video/webm,video/quicktime"
                      onChange={(e) => {
                        onVideoUpload(e);
                        setShowAttachMenu(false);
                      }}
                      style={{ display: 'none' }}
                    />
                  </label>
                </div>
              </>
            )}

            {/* Character count */}
            {prompt.length > 0 && (
              <span style={{
                fontSize: '0.75rem',
                color: prompt.length > RENDER_CONFIG.characterLimitWarning
                  ? colors.foreground
                  : colors.foregroundTertiary,
                fontWeight: prompt.length > RENDER_CONFIG.characterLimitWarning ? 500 : 400,
                marginLeft: spacingMobile.xs,
              }}>
                {prompt.length}/{RENDER_CONFIG.maxPromptLength}
              </span>
            )}
          </div>

          {/* Right Controls */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: spacingMobile.sm,
          }}>

            {hasHistory && (
              <>
                <SimpleTooltip content="Clear history">
                  <button
                    onClick={() => setShowClearConfirm(true)}
                    aria-label="Clear history"
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#FDE8E8';
                      e.currentTarget.style.color = '#C0392B';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.color = colors.foregroundTertiary;
                    }}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 36,
                      height: 36,
                      padding: 0,
                      background: 'transparent',
                      border: 'none',
                      color: colors.foregroundTertiary,
                      cursor: 'pointer',
                      borderRadius: 8,
                      transition: 'all 0.12s ease'
                    }}
                  >
                    <Trash2 size={16} />
                  </button>
                </SimpleTooltip>

                <ConfirmModal
                  isOpen={showClearConfirm}
                  title="Clear chat history"
                  description="This will remove all messages and reset the current session. This cannot be undone."
                  confirmLabel="Clear"
                  cancelLabel="Cancel"
                  danger={true}
                  onCancel={() => setShowClearConfirm(false)}
                  onConfirm={() => {
                    setShowClearConfirm(false);
                    onClearHistory();
                  }}
                />
              </>
            )}

            {/* Submit Button */}
            <button
              onClick={onGenerate}
              disabled={!canSubmit}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '40px',
                height: '40px',
                padding: 0,
                background: canSubmit ? '#6D916C' : colors.backgroundOffset,
                border: 'none',
                borderRadius: borderRadius.sm,
                cursor: canSubmit ? 'pointer' : 'not-allowed',
                color: canSubmit ? 'white' : colors.foregroundTertiary,
                transition: 'all 0.15s ease',
              }}
            >
              {isGenerating ? (
                <>
                  <style>{`
                    @keyframes spin {
                      to { transform: rotate(360deg); }
                    }
                  `}</style>
                  <div style={{
                    width: '20px',
                    height: '20px',
                    border: '2px solid rgba(255,255,255,0.3)',
                    borderTopColor: 'white',
                    borderRadius: '50%',
                    animation: 'spin 0.8s linear infinite',
                  }} />
                </>
              ) : (
                <ArrowUp size={22} />
              )}
            </button>
          </div>
        </div>

        {/* Disabled timer message */}
        {isDisabled && remainingTime && (
          <div style={{
            padding: `${spacingMobile.sm} ${spacingMobile.md}`,
            borderTop: `1px solid ${colors.border}`,
            fontSize: '0.75rem',
            color: colors.foregroundSecondary,
            textAlign: 'center',
          }}>
            Available in {remainingTime}
          </div>
        )}
        </div>
        </>
        )}

        {/* Quick Actions - only show if no history and no uploaded media */}
        {!hasHistory && !hasUploadedMedia && (
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: spacingMobile.sm,
            marginTop: spacingMobile.lg,
            paddingBottom: spacingMobile.xl,
          }}>
            {quickActions.map((action, index) => {
              const Icon = action.icon;
              return (
                <button
                  key={index}
                  onClick={() => handleQuickAction(action.prompt)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: spacingMobile.xs,
                    padding: `${spacingMobile.sm} ${spacingMobile.md}`,
                    background: colors.backgroundPanel,
                    border: `1px solid ${colors.border}`,
                    borderRadius: borderRadius.full,
                    cursor: 'pointer',
                    color: colors.foreground,
                    fontSize: '0.8125rem',
                    fontWeight: 400,
                    transition: 'all 0.15s ease',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <Icon size={14} style={{ opacity: 0.7 }} />
                  <span>{action.label}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default MobileCreativePromptView;
